import { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple in-memory cache so repeated component mounts don't re-fetch.
// Keyed per slug+language so switching language re-fetches the resolved copy.
const _cache = {};

const CMS_ASSET_RENAMES = {
  '/assets/photos/site-updates/classroom-group-selfie.png': '/assets/photos/site-updates/classroom-group-selfie.webp',
  '/assets/photos/site-updates/classroom-teachers-reference.png': '/assets/photos/site-updates/classroom-teachers-reference.webp',
  '/assets/photos/site-updates/flankly.png': '/assets/photos/site-updates/flankly.webp',
  '/assets/photos/site-updates/group-of-five-alternative.png': '/assets/photos/site-updates/group-of-five-alternative.webp',
  '/assets/photos/site-updates/hero-cida.png': '/assets/photos/site-updates/hero-cida.webp',
  '/assets/photos/site-updates/hero-with-final-touches.png': '/assets/photos/site-updates/hero-final-touches.webp',
  '/assets/photos/site-updates/IN LOCO TRAINING-PERFECT ONE.jpeg': '/assets/photos/site-updates/in-loco-training-perfect-one.webp',
  '/assets/photos/site-updates/online-worldwide-working.jpeg': '/assets/photos/site-updates/online-worldwide-working.webp',
  '/assets/photos/site-updates/Open page.jpeg': '/assets/photos/site-updates/open-page.webp',
  '/assets/photos/site-updates/PERFECT ACADEMIC WRITING PICTURE 26.05.jpeg': '/assets/photos/site-updates/perfect-academic-writing.webp',
  '/assets/photos/site-updates/professional-good-one.png': '/assets/photos/site-updates/professional-good-one.webp',
  '/assets/photos/site-updates/pronunciation-background-board.jpeg': '/assets/photos/site-updates/pronunciation-background-board.webp',
  '/assets/photos/site-updates/Speaking Development.jpeg': '/assets/photos/site-updates/speaking-development.webp',
  '/assets/photos/site-updates/student-results-certificates.png': '/assets/photos/site-updates/student-results-certificates.webp',
  '/assets/photos/site-updates/Teaching  style.jpeg': '/assets/photos/site-updates/teaching-style.webp',
  '/assets/photos/site-updates/teaching-a-couple-perfect-one.png': '/assets/photos/site-updates/teaching-a-couple-perfect-one.webp',
  '/assets/photos/site-updates/teaching-hero.png': '/assets/photos/site-updates/teaching-hero.webp',
  '/assets/photos/site-updates/teaching-small-groups.png': '/assets/photos/site-updates/teaching-small-groups.webp',
  '/assets/photos/site-updates/Working.jpeg': '/assets/photos/site-updates/working.webp',
  'https://images.pexels.com/photos/4226122/pexels-photo-4226122.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940': '/assets/photos/site-updates/flankly.webp',
  'https://images.pexels.com/photos/7437492/pexels-photo-7437492.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940': '/assets/photos/site-updates/classroom-teachers-reference.webp',
  'https://images.unsplash.com/photo-1565598621680-94ac0c22b148?crop=entropy&cs=srgb&fm=jpg&q=85&w=800': '/assets/photos/site-updates/perfect-academic-writing.webp',
};

const normalizeCmsValue = (value) => {
  if (typeof value === 'string') return CMS_ASSET_RENAMES[value] || value;
  if (Array.isArray(value)) return value.map(normalizeCmsValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, normalizeCmsValue(entry)])
    );
  }
  return value;
};

/**
 * Fetch CMS content for a page slug and expose helper functions.
 *
 * Usage:
 *   const cms = useCmsContent('home');
 *   cms.get('hero', 'title', 'Default headline')
 *   cms.getList('features', DEFAULT_FEATURES)   // returns items array
 */
export const useCmsContent = (slug) => {
  const { language } = useLanguage();
  const cacheKey = `${slug}:${language}`;
  const [sections, setSections] = useState(_cache[cacheKey] || null);

  useEffect(() => {
    if (!slug) return;
    if (_cache[cacheKey]) {
      setSections(_cache[cacheKey]);
      return;
    }
    axios
      .get(`${API_URL}/api/public/content/${slug}`, { params: { lang: language } })
      .then(res => {
        _cache[cacheKey] = res.data || {};
        setSections(_cache[cacheKey]);
      })
      .catch(() => {
        _cache[cacheKey] = {};
        setSections({});
      });
  }, [slug, language, cacheKey]);

  /**
   * Get a single field value from a section.
   * Falls back to `fallback` if CMS has no value.
   */
  const get = (sectionKey, fieldKey, fallback = '') => {
    if (!sections) return fallback;
    const val = sections[sectionKey]?.[fieldKey];
    return val !== undefined && val !== '' ? normalizeCmsValue(val) : fallback;
  };

  /**
   * Get a repeater list (stored as `items` inside the section content).
   * Falls back to `defaultList` if CMS has no items saved.
   */
  const getList = (sectionKey, defaultList = []) => {
    if (!sections) return defaultList;
    const items = sections[sectionKey]?.items;
    return Array.isArray(items) && items.length > 0 ? normalizeCmsValue(items) : defaultList;
  };

  return { sections, get, getList, loading: sections === null };
};

/**
 * Invalidate the in-memory cache for a page slug so the next
 * component mount re-fetches fresh data from the server.
 * Call this after saving CMS content in AdminCMS.
 */
export const clearCmsCache = (slug) => {
  if (slug) {
    // cache keys are `${slug}:${language}` — clear every language for this slug
    Object.keys(_cache).forEach(k => {
      if (k === slug || k.startsWith(`${slug}:`)) delete _cache[k];
    });
  } else {
    Object.keys(_cache).forEach(k => delete _cache[k]);
  }
};

export default useCmsContent;
